import { aiSkillOn, aiSkillWaitsForMock, aiSprintOn, aiSprintWaitsForMock, type AiSkill } from './aiSkills';
import { isFeatureOn } from './appConfig';

/**
 * Закрытые входы ученика: то, что решено спрятать, но не удалять.
 *
 * ⚠️ AI SPEAKING (решение владельца D22, 11.09.2026). Свободный разговор с ИИ
 * дорогой — около доллара за сессию. Ученик не видит его ни в одном месте, а по
 * прямому адресу попадает в тренажёры с объяснением «Speaking доступен в
 * Mock-тесте». Внутри Mock та же речевая часть работает как раньше.
 *
 * ⚠️ ПОЧЕМУ НЕ ТАРИФНЫЙ КЛЮЧ. `speaking_ai` и `ielts_speaking` на сервере
 * открывают и речь в Mock: мок отбирает свои части по тем же ключам. Выключи
 * их — у мока пропадёт Speaking. Поэтому здесь отдельный ключ, который знает
 * только разметка: он прячет входы и больше ничего.
 *
 * ⚠️ ЗАКРЫТО УМОЛЧАНИЕМ В КОДЕ (`show_ai_speaking: false` в appConfig). Сервер
 * такого ключа не шлёт, значит вход закрыт без всякой настройки. Вернуть — ключ
 * `features.show_ai_speaking = true` в документе настроек, без сборки.
 *
 * Сторож новых ссылок — `app/aiEntriesGuard.test.ts`.
 */

/** Где ученик мог попасть в разговор с ИИ. */
const AI_SPEAKING_PATHS = [
  // Тренажёр речи в хабе и на главной.
  '/app/trainers/ielts/speaking',
  // Экран разговора с ИИ-экзаменатором.
  '/app/ielts/speaking',
  // Карточка «AI Speaking» программ без IELTS. Сам адрес не закрывается: там же
  // вкладка разговорных комнат, а комнаты — живые занятия, их не трогаем.
  '/app/speaking',
];

/**
 * ИИ-тренажёры IELTS — по навыкам (12–13.10.2026, docs/CONTRACTS/ai-skills.md).
 *
 * ⚠️ Каждый вход закрывает СВОЙ навык из ручки `GET /ielts/ai-skills`, где сервер
 * уже свёл тумблер сервера, тумблер курса, тариф и сданный мок (`lib/aiSkills.ts`).
 * До 12.10 все четыре входа закрывал один ключ `show_ai_trainers` (D26) — он
 * остался только для приложения 1.1.1, этот код его не читает.
 *
 * ⚠️ НАВЫК, ЖДУЩИЙ МОКА, — НЕ ЗАКРЫТЫЙ ВХОД. Включённый, но не сданный навык
 * остаётся видимым: плитка под замком «сначала пробный экзамен», страница
 * тренажёра по ответу 409 зовёт на мок. Прячется только невключённый.
 *
 * ⚠️ КАТАЛОГ `/app/ielts/reading|listening|writing` СЮДА НЕ ВХОДИТ НАМЕРЕННО
 * (решение владельца 11.09): на него ведут задания куратора, и собран он не ИИ.
 * Мок и хаб — тоже нет: мок собирается сервером из включённых навыков.
 */
const AI_TRAINER_SKILL: Record<string, AiSkill> = {
  '/app/trainers/ielts/reading': 'reading',
  '/app/trainers/ielts/listening': 'listening',
  '/app/trainers/ielts/writing': 'writing',
  // Речь закрыта ещё и ключом D22 (AI_SPEAKING_PATHS выше) — проверяется первым.
  '/app/trainers/ielts/speaking': 'speaking',
};
const SPRINT_PATH = '/app/ielts/sprint';

export type ClosedEntry = 'speaking' | 'trainers';

export function aiSpeakingShown(): boolean {
  return isFeatureOn('show_ai_speaking');
}

/**
 * Виден ли хоть один тренажёр навыка — открытый или ждущий мока (для
 * заголовков и заглушки хаба: «ждёт мока» — не «скоро вернутся»).
 */
export function aiTrainersShown(): boolean {
  return (['reading', 'listening', 'writing', 'speaking'] as AiSkill[]).some((k) => aiSkillOn(k) || aiSkillWaitsForMock(k));
}

/** Путь без параметров, якоря и хвостовой черты. */
function bare(path: string): string {
  return path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
}

/** Какой закрытый вход скрывается за адресом, или `null`, если адрес открыт. */
export function closedEntry(path: string): ClosedEntry | null {
  const p = bare(path);
  if (!aiSpeakingShown() && AI_SPEAKING_PATHS.includes(p)) return 'speaking';
  const skill = AI_TRAINER_SKILL[p];
  if (skill && !aiSkillOn(skill) && !aiSkillWaitsForMock(skill)) return 'trainers';
  if (p === SPRINT_PATH && !aiSprintOn() && !aiSprintWaitsForMock()) return 'trainers';
  return null;
}

/** Куда уводить с закрытого адреса. `null` — адрес открыт. */
export function closedRedirect(path: string): string | null {
  const entry = closedEntry(path);
  return entry ? `/app/trainers?closed=${entry}` : null;
}

export function entryHidden(path: string): boolean {
  return closedEntry(path) !== null;
}
/**
 * Список ссылок без закрытых входов, порядок прежний.
 *
 * ⚠️ Отбирает по адресу, а не по названию карточки: одна и та же страница
 * бывает в разных списках под разными подписями.
 */
export function withoutClosed<T>(items: readonly T[], to: (item: T) => string = (i) => (i as { to: string }).to): T[] {
  return items.filter((item) => !entryHidden(to(item)));
}
